import { getCurrency } from '@common/utils/currency.ts'
import type { ColumnDef } from '@tanstack/react-table'
import { format } from 'date-fns'

import OFIcon from '@/assets/icons/icon-only-fans.svg'
import { CreatorBrowserLink } from '@/components/ui/CreatorBrowserLink.tsx'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar.tsx'
import { unEscape } from '@/utils/helpers'

import { isFailedResult, isFollowedResult } from '../guards'
import type { AutoFollowFan, AutoFollowResponses } from '../types'
import type { AutoFollowTableKeys } from './use-auto-follow-table'

const createCommonColumns = (): ColumnDef<AutoFollowFan>[] => [
  {
    accessorKey: 'username',
    header: 'Fan Name',
    cell: ({ row }) => {
      const displayName = row.original.displayName
      const avatarImage = row.original.avatar

      return (
        <div className="flex gap-3">
          <Avatar>
            {avatarImage ? (
              <AvatarImage
                src={avatarImage}
                alt={displayName}
              />
            ) : (
              <AvatarFallback className="bg-primary-500 text-white">
                {displayName.charAt(0).toUpperCase()}
              </AvatarFallback>
            )}
          </Avatar>
          <div>
            <p className="text-base font-medium text-title">
              {unEscape(displayName)}
            </p>
            <p className="text-xs font-medium text-secondary-300">
              @{row.original.username}
            </p>
          </div>
        </div>
      )
    },
  },
  {
    header: 'Total Spend',
    accessorKey: 'totalSpend',
    cell: ({ row }) => <span>{getCurrency(row.original.totalSpend)}</span>,
  },
]

const actionColumn = (): ColumnDef<AutoFollowFan> => ({
  id: 'action',
  header: () => <span className="w-full text-right">Action</span>,
  cell: ({ row }) => (
    <CreatorBrowserLink href={'https://onlyfans.com/' + row.original.username}>
      <a
        className="float-right flex items-center gap-1 text-primary-500 hover:underline"
        rel="noopener noreferrer"
        target="_blank"
      >
        <OFIcon
          width={16}
          height={16}
          className="fill-primary-500"
        />
        View Profile
      </a>
    </CreatorBrowserLink>
  ),
})

export const useBuildAutoFollowColumns = (
  data: AutoFollowResponses | undefined,
  selectedTable: AutoFollowTableKeys,
): ColumnDef<any>[] => {
  const columns: ColumnDef<any>[] = [...createCommonColumns()]

  if (data) {
    if (selectedTable === 'followed' && isFollowedResult(data)) {
      columns.push({
        accessorKey: 'followedAt',
        header: 'Followed',
        cell: ({ row }) => (
          <span>{format(new Date(row.original.followedAt), 'LLL dd, y')}</span>
        ),
      })
    }

    if (selectedTable === 'failed' && isFailedResult(data)) {
      columns.push({
        accessorKey: 'failedAt',
        header: 'Follow Try',
        cell: ({ row }) => (
          <span>{format(new Date(row.original.failedAt), 'LLL dd, y')}</span>
        ),
      })
    }
  }

  columns.push(actionColumn())

  return columns
}
